import { useQuery } from '@apollo/client'
import {
    Edit,
    KeyboardArrowLeft,
    KeyboardArrowRight,
} from '@mui/icons-material'
import {
    Box,
    Button,
    Divider,
    IconButton,
    MobileStepper,
    Stack,
    Typography,
    useTheme,
} from '@mui/material'
import { gql } from '__generated__'
import { type GetCandidateReferencesQuery } from '__generated__/graphql'
import { Fragment, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { ProfilePanel } from 'recruiter/components/ProfilePanel'
import useToast from 'shared/components/Toast/useToast'
import { formatGraphQLError, getPxAsRem } from 'shared/utils'
import { CandidateReferenceDrawer } from './CandidateReferenceDrawer'

export const GetCandidateReferences = gql(/* GraphQL */ `
    query GetCandidateReferences($candidateId: ID) {
        candidate(id: $candidateId) {
            id
            references {
                id
                firstName
                lastName
                email
                phone
                company {
                    id
                    value
                }
                workTitle {
                    id
                    value
                }
            }
        }
    }
`)

export type Reference = NonNullable<
    NonNullable<GetCandidateReferencesQuery['candidate']>['references']
>[number]

interface CandidateReferencesPanelProps {}

export const CandidateReferencesPanel: React.FC<
    CandidateReferencesPanelProps
> = () => {
    const { candidateId } = useParams()
    const toast = useToast()
    const theme = useTheme()
    const [activeStep, setActiveStep] = useState(0)
    const [selectedReference, setSelectedReference] =
        useState<Reference | null>(null)
    const [isReferenceDrawerOpen, setIsReferenceDrawerOpen] = useState(false)
    const [references, setReferences] = useState<Reference[]>([])

    useQuery(GetCandidateReferences, {
        fetchPolicy: 'cache-and-network',
        variables: { candidateId },
        onCompleted: (data) => {
            setReferences(data.candidate?.references ?? [])
        },
        onError: (error) => {
            toast({
                title: 'Error',
                message: formatGraphQLError(
                    error,
                    'Error fetching candidate references'
                ),
                severity: 'error',
            })
        },
    })

    const itemsPerPage = 3

    const totalSteps = Math.ceil(references.length / itemsPerPage)

    const handleNext = () => {
        setActiveStep((prevActiveStep) => prevActiveStep + 1)
    }

    const handleBack = () => {
        setActiveStep((prevActiveStep) => prevActiveStep - 1)
    }

    const startIndex = activeStep * itemsPerPage
    const endIndex = startIndex + itemsPerPage
    const currentReferences = references.slice(startIndex, endIndex)
    const referenceSpacingPixels = 16

    return (
        <>
            <CandidateReferenceDrawer
                isOpen={isReferenceDrawerOpen}
                reference={selectedReference}
                onClose={() => {
                    setIsReferenceDrawerOpen(false)
                    setSelectedReference(null)
                }}
            />

            <ProfilePanel
                panelTitle="References"
                actionElements={[
                    <Button
                        key="add-reference"
                        variant="outlined"
                        onClick={() => setIsReferenceDrawerOpen(true)}
                    >
                        Add Reference
                    </Button>,
                ]}
            >
                <Stack direction="row" gap={getPxAsRem(referenceSpacingPixels)}>
                    {currentReferences.map((reference, index) => {
                        const isFirstOnPage = index % 3 !== 0
                        const dividerCount = currentReferences.length - 1
                        // Calculate the total space from the edge of one
                        // reference to the next so their widths can be
                        // distributed evenly.
                        const spaceBetweenReferencesPixels =
                            dividerCount *
                            (referenceSpacingPixels * 2 + dividerCount)

                        return (
                            <Fragment key={reference.id}>
                                {isFirstOnPage && (
                                    <Divider orientation="vertical" flexItem />
                                )}

                                <Box
                                    sx={{
                                        flex: '1 1 0px',
                                        maxWidth: `calc((100% - ${spaceBetweenReferencesPixels}px) / ${currentReferences.length})`,
                                    }}
                                >
                                    <Box
                                        display="flex"
                                        alignItems="center"
                                        flexDirection="row"
                                    >
                                        <Typography
                                            variant="body1"
                                            fontWeight="500"
                                            color={theme.palette.grey[600]}
                                            noWrap
                                            mr={3}
                                        >
                                            {`Reference ${references.indexOf(reference) + 1}`}
                                        </Typography>
                                        <IconButton
                                            onClick={() => {
                                                setSelectedReference(reference)
                                                setIsReferenceDrawerOpen(true)
                                            }}
                                        >
                                            <Edit fontSize="small" />
                                        </IconButton>
                                    </Box>

                                    <Typography variant="subtitle2" noWrap>
                                        {reference.firstName}{' '}
                                        {reference.lastName}
                                    </Typography>

                                    <Typography variant="body2" noWrap>
                                        {reference?.workTitle?.value}
                                    </Typography>

                                    <Typography variant="body2" noWrap>
                                        <Link
                                            to={`/companies/${reference?.company?.id}`}
                                            style={{
                                                textDecoration: 'none',
                                                color: theme.palette.primary
                                                    .main,
                                            }}
                                        >
                                            {reference?.company?.value}
                                        </Link>
                                    </Typography>

                                    <Typography variant="body2" noWrap>
                                        {reference?.phone}
                                    </Typography>

                                    <Typography variant="body2" noWrap>
                                        <a
                                            href={`mailto:${reference?.email}`}
                                            style={{
                                                textDecoration: 'none',
                                                color: theme.palette.primary
                                                    .main,
                                            }}
                                        >
                                            {reference?.email}
                                        </a>
                                    </Typography>
                                </Box>
                            </Fragment>
                        )
                    })}
                </Stack>

                <MobileStepper
                    variant="dots"
                    steps={totalSteps}
                    position="static"
                    activeStep={activeStep}
                    sx={{
                        flexGrow: 1,
                        background: 'transparent',
                        boxShadow: 'none',
                        padding: 0,
                    }}
                    nextButton={
                        <Button
                            size="small"
                            variant="text"
                            onClick={handleNext}
                            disabled={activeStep === totalSteps - 1}
                        >
                            Next
                            {theme.direction === 'rtl' ? (
                                <KeyboardArrowLeft />
                            ) : (
                                <KeyboardArrowRight />
                            )}
                        </Button>
                    }
                    backButton={
                        <Button
                            size="small"
                            variant="text"
                            onClick={handleBack}
                            disabled={activeStep === 0}
                        >
                            {theme.direction === 'rtl' ? (
                                <KeyboardArrowRight />
                            ) : (
                                <KeyboardArrowLeft />
                            )}
                            Back
                        </Button>
                    }
                />
            </ProfilePanel>
        </>
    )
}
